import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Full-width banner at the top of the dashboard when there are 1+
 * maintenance requests still in the "not_started" state.
 * Expands to show a per-location breakdown; clicking a location routes to
 * that location's requests page. Auto-hides at zero.
 */
const UnaddressedRequestsBanner = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [locations, setLocations] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const [reqRes, locRes] = await Promise.all([
          axios.get(`${API}/maintenance-requests`),
          axios.get(`${API}/locations`),
        ]);
        if (cancelled) return;
        const unaddressed = (reqRes.data || []).filter((r) => r.status === 'not_started');
        setRequests(unaddressed);
        setLocations(locRes.data || []);
      } catch (_e) {
        // Silent -- the banner is nice-to-have.
      }
    };
    fetchData();
    const t = setInterval(fetchData, 60000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const count = requests.length;
  if (count === 0) return null;

  // Group unaddressed requests by plant_location_id. `null` (Unassigned
  // Equipment bucket) is normalized to the 'unassigned' sentinel so the
  // click-through URL doesn't blow up on /location/null/… — the requests
  // page recognizes 'unassigned' as a special view.
  const byLocation = requests.reduce((acc, r) => {
    const key = r.plant_location_id || 'unassigned';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});
  const locNameFor = (id) =>
    id === 'unassigned' ? 'Unassigned Equipment' : (locations.find((l) => l.id === id)?.name || 'Unassigned Equipment');

  return (
    <div
      data-testid="unaddressed-requests-banner"
      className="w-full mb-4 rounded-lg border border-amber-300 bg-amber-50 shadow-sm"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left transition hover:bg-amber-100"
        data-testid="unaddressed-requests-toggle"
      >
        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-amber-900">
            {count} maintenance {count === 1 ? 'request' : 'requests'} awaiting review
          </div>
          <div className="text-xs text-amber-800/80 mt-0.5">
            {expanded ? 'Collapse to hide' : 'Click to expand and triage'}
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-amber-700" />
        ) : (
          <ChevronDown className="h-4 w-4 text-amber-700" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-amber-200 divide-y divide-amber-100 bg-white/60">
          {Object.entries(byLocation).map(([locId, locRequests]) => (
            <button
              key={locId}
              type="button"
              onClick={() => navigate(`/location/${locId}/maintenance-requests`)}
              data-testid={`banner-goto-location-${locId}`}
              className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-amber-50 transition"
            >
              <div>
                <div className="text-sm font-medium text-slate-900">{locNameFor(locId)}</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  {locRequests.length} awaiting {locRequests.length === 1 ? 'review' : 'reviews'}
                </div>
              </div>
              <div className="text-xs font-medium text-amber-700">Review &rarr;</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default UnaddressedRequestsBanner;
